import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  Box,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";

toast.configure();

const fmt2 = (v) => Number(v || 0).toFixed(2);

const BizonetSync = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const redirectToLoginPage = () => {
    if (!userToken) return <Redirect exact to="/sparepartsLogin" />;
    return null;
  };

  const apiBase = process.env.REACT_APP_API_BASE || "http://localhost:5000";

  const [loadingKey, setLoadingKey] = useState(false);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);

  const [bizData, setBizData] = useState(null);
  const [syncResp, setSyncResp] = useState(null);

  const bizousername = "GNRXTVS1";
  const bizopassword = "258";

  const [open, setOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const [openMissing, setOpenMissing] = useState(false);

  // IMPORTANT: selection model (string[])
  const [selectedIds, setSelectedIds] = useState([]);

  // Shift + J toggle JSON
  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.key.toLowerCase() === "j") {
        setShowJson((prev) => !prev);
        toast.info("JSON view toggled");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const getApiKey = () => (localStorage.getItem("x-api-key") || "").trim();

  const handleGenerateApiKey = async () => {
    setLoadingKey(true);
    try {
      const res = await fetch(`/bizonet/apikey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken, // change to jjtoken if your middleware expects jjtoken
        },
        body: JSON.stringify({
          username: bizousername,
          password: bizopassword,
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(data?.message || "Failed to generate apiKey");

      if (!data?.apiKey) throw new Error("apiKey missing in response");
      localStorage.setItem("x-api-key", data.apiKey);

      toast.success("API key generated");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to generate API key");
    } finally {
      setLoadingKey(false);
    }
  };

  const handleFetchVouchers = async () => {
    setLoadingVouchers(true);
    setSyncResp(null);
    try {
      const apiKey = getApiKey();
      if (!apiKey) {
        toast.error("Generate API key first");
        return;
      }

      const res = await fetch(`/bizonet/vouchers/transform`, {
        method: "GET",
        headers: {
          sptoken: userToken, // change to jjtoken if needed
          "x-api-key": apiKey,
        },
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Fetch failed");

      setBizData(data);

      // reset selection on refresh
      setSelectedIds([]);

      toast.success(`Fetched ${data?.count ?? 0} vouchers`);

      if ((data?.missingItems || []).length > 0) {
        toast.warn(`Missing items: ${data.missingItems.length}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Fetch failed");
    } finally {
      setLoadingVouchers(false);
    }
  };

  // ✅ FIX: stable id ONLY = sorton
  const headerRows = useMemo(() => {
    return (bizData?.headerdata || [])
      .filter((h) => h?.sorton)
      .map((h) => ({
        id: String(h.sorton), // ✅ MUST be string
        ...h,
      }));
  }, [bizData]);

  const detailByKey = useMemo(() => {
    const map = new Map();
    (bizData?.detaildata || []).forEach((x) => {
      map.set(String(x.bizSyncVchId), x.details || []);
    });
    return map;
  }, [bizData]);

  const syncStatusMap = useMemo(() => {
    const m = new Map();
    const list = syncResp?.Bizonet?.bizSyncVchIds || [];
    for (const x of list) {
      const k = String(x.bizSyncVchId || "").trim();
      if (!k) continue;
      m.set(k, x);
    }
    return m;
  }, [syncResp]);

  const headerCols = useMemo(
    () => [
      {
        field: "__status",
        headerName: "Status",
        width: 130,
        sortable: false,
        renderCell: (p) => {
          const k = String(p.row.id || "");
          const st = syncStatusMap.get(k);
          if (!st)
            return <Chip size="small" label="Pending" variant="outlined" />;
          const ok = String(st.status || "").toLowerCase() === "completed";
          return (
            <Chip
              size="small"
              label={ok ? "Completed" : "Error"}
              color={ok ? "success" : "error"}
              variant="filled"
            />
          );
        },
      },
      { field: "billdate", headerName: "Date", width: 110 },
      { field: "billno", headerName: "Bill No", width: 150 },
      { field: "shpname", headerName: "Party", width: 280 },

      {
        field: "totgross",
        headerName: "Gross",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "totdiscount",
        headerName: "Disc",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "tottaxable",
        headerName: "Taxable",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "totcgst",
        headerName: "CGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "totsgst",
        headerName: "SGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "totigst",
        headerName: "IGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "totroundoff",
        headerName: "Round",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "totbillamt",
        headerName: "Net",
        width: 120,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "view",
        headerName: "View",
        width: 90,
        sortable: false,
        renderCell: (p) => (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHeader(p.row);
              setOpen(true);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [syncStatusMap]
  );

  const details = useMemo(() => {
    if (!selectedHeader) return [];
    const key = String(selectedHeader.id || "");
    const raw = detailByKey.get(key) || [];

    return raw.map((it, i) => ({
      id: `${key}_${it.srlno || i}`,
      itemcode: it.partno || "",
      itemname: it.stockItem || "",
      ...it,
      mrp_calc: it.mrp_calc ?? it.mrp ?? 0,
    }));
  }, [selectedHeader, detailByKey]);

  const detailCols = useMemo(
    () => [
      { field: "srlno", headerName: "#", width: 60 },
      { field: "itemcode", headerName: "Item Code", width: 140 },
      { field: "itemname", headerName: "Item Name", width: 300 },
      { field: "itemid", headerName: "ItemId", width: 90, hide: true },

      {
        field: "saleqty",
        headerName: "Qty",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "salerate",
        headerName: "Rate",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "gstrate",
        headerName: "GST%",
        width: 80,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "mrp_calc",
        headerName: "MRP",
        width: 100,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "grossvalue",
        headerName: "Gross",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "discountrate",
        headerName: "Disc%",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "discountvalue",
        headerName: "Disc Amt",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "taxablevalue",
        headerName: "Taxable",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "cgstvalue",
        headerName: "CGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "sgstvalue",
        headerName: "SGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },
      {
        field: "igstvalue",
        headerName: "IGST",
        width: 90,
        valueFormatter: (p) => fmt2(p.value),
      },

      {
        field: "netvalue",
        headerName: "Net",
        width: 110,
        valueFormatter: (p) => fmt2(p.value),
      },
      { field: "hsncode", headerName: "HSN", width: 120 },
    ],
    []
  );

  const jsonBundle = useMemo(() => {
    if (!selectedHeader) return null;
    return { header: selectedHeader, details };
  }, [selectedHeader, details]);

  const missingFromTransform = bizData?.missingItems || [];
  const missingFromSync = useMemo(() => {
    const out = [];
    const rs = syncResp?.results || [];
    for (const r of rs) {
      (r?.missingItems || []).forEach((m) => out.push(m));
    }
    return out;
  }, [syncResp]);

  const missingRows = useMemo(() => {
    const merged = [...missingFromTransform, ...missingFromSync];
    return merged.map((m, i) => ({
      id: `${m.partno || "NA"}_${i}`,
      ...m,
    }));
  }, [missingFromTransform, missingFromSync]);

  const missingCols = useMemo(
    () => [
      { field: "partno", headerName: "PartNo", width: 180 },
      { field: "stockItem", headerName: "Item Name (Bizonet)", width: 320 },
      { field: "bizSyncVchId", headerName: "Voucher Key", width: 400 },
      { field: "billno", headerName: "Bill No", width: 160 },
    ],
    []
  );

  const buildVoucherPack = (hdrRow) => {
    const key = String(hdrRow.id || "");
    const det = detailByKey.get(key) || [];
    return { header: hdrRow, details: det };
  };

  const handleSyncSelected = async () => {
    try {
      const apiKey = getApiKey();
      if (!apiKey) return toast.error("Generate API key first");

      if (!headerRows || headerRows.length === 0)
        return toast.error("No vouchers fetched");
      if (!selectedIds || selectedIds.length === 0)
        return toast.error("Select at least one voucher");

      const packs = selectedIds
        .map((id) => headerRows.find((x) => String(x.id) === String(id)))
        .filter(Boolean)
        .map((h) => buildVoucherPack(h));

      if (packs.length === 0) return toast.error("No valid selection");

      setLoadingSync(true);

      const res = await fetch(`/bizonet/salebill/insert-and-sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
          "x-api-key": apiKey,
        },
        body: JSON.stringify({ vouchers: packs }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok && res.status !== 207)
        throw new Error(data?.message || "Sync failed");

      setSyncResp(data);

      const list = data?.Bizonet?.bizSyncVchIds || [];
      const okCnt = list.filter(
        (x) => String(x.status).toLowerCase() === "completed"
      ).length;
      const errCnt = list.filter(
        (x) => String(x.status).toLowerCase() === "error"
      ).length;

      if (errCnt > 0)
        toast.warn(`Sync done. Completed: ${okCnt}, Errors: ${errCnt}`);
      else toast.success(`Sync done. Completed: ${okCnt}`);

      if (missingRows.length > 0) setOpenMissing(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Sync failed");
    } finally {
      setLoadingSync(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      const apiKey = getApiKey();
      if (!apiKey) return toast.error("Generate API key first");

      if (!headerRows || headerRows.length === 0)
        return toast.error("No vouchers fetched");

      const packs = headerRows.map((h) => buildVoucherPack(h));

      setLoadingSync(true);

      const res = await fetch(`/bizonet/salebill/insert-and-sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
          "x-api-key": apiKey,
        },
        body: JSON.stringify({ vouchers: packs }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok && res.status !== 207)
        throw new Error(data?.message || "Sync failed");

      setSyncResp(data);

      const list = data?.Bizonet?.bizSyncVchIds || [];
      const okCnt = list.filter(
        (x) => String(x.status).toLowerCase() === "completed"
      ).length;
      const errCnt = list.filter(
        (x) => String(x.status).toLowerCase() === "error"
      ).length;

      if (errCnt > 0)
        toast.warn(`Sync done. Completed: ${okCnt}, Errors: ${errCnt}`);
      else toast.success(`Sync done. Completed: ${okCnt}`);

      if (missingRows.length > 0) setOpenMissing(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Sync failed");
    } finally {
      setLoadingSync(false);
    }
  };
  useEffect(() => {
    console.log("selectedIds:", selectedIds);
  }, [selectedIds]);
  return (
    <>
      <Sidebar
        Title="BIZONET SYNC"
        expandedPanel="panel2"
        children={
          <Box p={2}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  onClick={handleGenerateApiKey}
                  disabled={loadingKey}
                >
                  {loadingKey ? "Generating..." : "Generate API Key"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleFetchVouchers}
                  disabled={loadingVouchers}
                >
                  {loadingVouchers ? "Fetching..." : "Fetch Vouchers"}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSyncSelected}
                  disabled={loadingSync}
                >
                  {loadingSync ? "Syncing..." : "Sync Selected"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleSyncAll}
                  disabled={loadingSync}
                >
                  Sync All
                </Button>

                {(missingRows || []).length > 0 && (
                  <Chip
                    label={`Missing: ${missingRows.length}`}
                    color="warning"
                    onClick={() => setOpenMissing(true)}
                    sx={{ ml: 1 }}
                  />
                )}

                <Typography
                  variant="caption"
                  sx={{ ml: "auto" }}
                  color="text.secondary"
                >
                  Tip: Shift + J toggles JSON in details
                </Typography>
              </Stack>

              <Box height={540}>
                <DataGrid
                  rows={headerRows}
                  columns={headerCols}
                  getRowId={(row) => row.id} // ✅ force stable row id
                  checkboxSelection
                  disableRowSelectionOnClick
                  density="compact"
                  // ✅ v6+
                  rowSelectionModel={selectedIds}
                  onRowSelectionModelChange={(m) => {
                    setSelectedIds((m || []).map((x) => String(x)));
                  }}
                  // ✅ v5 fallback
                  selectionModel={selectedIds}
                  onSelectionModelChange={(m) => {
                    const arr = Array.isArray(m) ? m : [m];
                    setSelectedIds(arr.map((x) => String(x)));
                  }}
                  onRowDoubleClick={(p) => {
                    setSelectedHeader(p.row);
                    setOpen(true);
                  }}
                />
              </Box>

              {syncResp?.Bizonet?.bizSyncVchIds?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Last sync response: {syncResp?.Bizonet?.bizSyncVchIds?.length}{" "}
                  voucher(s)
                </Typography>
              )}
            </Stack>
          </Box>
        }
      />

      {redirectToLoginPage()}

      {/* DETAILS */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Voucher Details{" "}
          <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
            {selectedHeader?.billno} | {selectedHeader?.billdate} |{" "}
            {selectedHeader?.shpname}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box height={380}>
            <DataGrid
              rows={details}
              columns={detailCols}
              disableRowSelectionOnClick
              density="compact"
            />
          </Box>

          {showJson && (
            <Box
              mt={2}
              p={2}
              sx={{
                background: "#fafafa",
                fontFamily: "monospace",
                whiteSpace: "pre",
                maxHeight: 280,
                overflow: "auto",
                border: "1px solid #ddd",
              }}
            >
              {JSON.stringify(jsonBundle, null, 2)}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              if (!showJson) return;
              navigator.clipboard.writeText(
                JSON.stringify(jsonBundle, null, 2)
              );
              toast.success("JSON copied");
            }}
            disabled={!showJson}
          >
            Copy JSON
          </Button>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* MISSING ITEMS */}
      <Dialog
        open={openMissing}
        onClose={() => setOpenMissing(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Missing / Invalid Items</DialogTitle>
        <DialogContent dividers>
          <Box height={440}>
            <DataGrid
              rows={missingRows}
              columns={missingCols}
              disableRowSelectionOnClick
              density="compact"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const txt = JSON.stringify(
                missingRows.map(({ id, ...r }) => r),
                null,
                2
              );
              navigator.clipboard.writeText(txt);
              toast.success("Missing items JSON copied");
            }}
            disabled={missingRows.length === 0}
          >
            Copy Missing JSON
          </Button>
          <Button onClick={() => setOpenMissing(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BizonetSync;
